import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { from, Observable, of, Subscription } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

import { Auth, Hub } from 'aws-amplify';
import { INavData } from '../models';
import { AuthConfigurationService } from './../views/auth/auth-configuration.service';
import { SupplierService } from './../views/suppliers/supplier.service';
import { TenantConfigService } from './../views/tenant-config/tenant-config.service';
import { TenantEntitlementService } from './../views/auth/tenant-entitlement.service';

import { environment } from '../../environments/environment'
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { TaxCalculatorDialogComponent } from '../tax-calculator-dialog/tax-calculator-dialog.component';


@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
})
export class NavComponent implements OnInit, OnDestroy {
  loading$: Observable<boolean> = of(false);
  isAuthenticated$: Observable<Boolean> | undefined;
  username$: Observable<string> | undefined;
  companyName$: Observable<string> | undefined;
  isPosAdmin$: Observable<boolean> = of(false);
  isTenantAdmin$: Observable<boolean> = of(false);
  // Empty until applyPlanEntitlements runs (hide-by-default; no flash of off-plan items).
  public navItems: INavData[] = [];
  // True until entitlements resolve and the first filtered nav list is ready (sidebar spinner).
  navPermissionsLoading = true;
  homeUrl = '/products/dashboard-list';
  isTagsAllowed = false;
  isChatAllowed = false;
  // Chat Admin settings entry + /chat/admin (separate from Oscar sidebar).
  isChatAdminAllowed = false;
  isOpsAllowed = false;
  // Tenant config settings item (view and/or edit); not tied to users/ops.
  isTenantConfigAllowed = false;
  isPosAllowed = false;
  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );


  tenantName: string = ""; 

  // we populate this with the env configuration settings, 
  // so we can display a text message on the screen when it is non-prod env. 
  // this is in order to prevent working on a production env by mistake
  // 
  isProduction: boolean = true; // by default we assume it is production. for safety.  
  isSidenavOpen = true; // default when open

  direction: 'ltr' | 'rtl' = 'ltr';
  private entitlementSub: Subscription | undefined;
  private sessionAllowedSub: Subscription | undefined;

  // Prefetch TenantConfig only after JWT tenant matches; Hub signOut still clears cache.
  private readonly authHubListener = ({ payload }: { payload: { event: string } }) => {
    if (payload?.event === 'signOut') {
      this.tenantConfigSvc.clearTenantConfigCache();
    }
  };

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    public authConfigService: AuthConfigurationService,
    private translate: TranslateService,
    private supplierService: SupplierService,
    private tenantConfigSvc: TenantConfigService,
    private tenantEntitlements: TenantEntitlementService,
    private dialog: MatDialog,
    private title: Title,
    private snackBar: MatSnackBar
  ) {
    // this.configSvc.loadConfigurations().subscribe((val) => console.log(val));
    this.loading$ = this.router.events.pipe(
      filter(
        (e) =>
          e instanceof NavigationStart ||
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError
      ),
      map((e) => e instanceof NavigationStart)
    );
    const savedLang = localStorage.getItem('userLang');
    const lang = savedLang || 'en';
    this.translate.setDefaultLang('en');
    this.translate.use(lang);
    this.direction = lang === 'he' ? 'rtl' : 'ltr';
    // Set document direction on initialization
    document.documentElement.dir = this.direction;
    localStorage.setItem('direction', this.direction);
    
    this.translate.onLangChange.subscribe(event => {
      this.direction = event.lang === 'he' ? 'rtl' : 'ltr';
      // Update document direction when language changes
      document.documentElement.dir = this.direction;
      localStorage.setItem('direction', this.direction);
    });
  }

  @ViewChild('drawer') drawer!: MatSidenav;

  ngOnInit(): void {

    // set the isProduction var
    this.isProduction = environment.production;
    this.setNonProdBrowserTitle();
    this.translate.onLangChange.subscribe(() => this.setNonProdBrowserTitle());
    this.entitlementSub = this.tenantEntitlements.allowedActionsChanged$.subscribe(() => {
      this.applyPlanEntitlements();
    });
    this.applyPlanEntitlements();
    this.authConfigService.refreshAllowedActionsFromStoredTenant();
    this.sessionAllowedSub = this.authConfigService.sessionAllowed$.subscribe((allowed) => {
      if (allowed) {
        this.prefetchTenantConfigs();
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.closeSidenav();
      }
      if (event instanceof NavigationEnd) {
        this.applyPlanEntitlements();
      }
    });

    try {
      const s = Auth.currentSession().catch((err) => {
        console.log('Failed to get current session. Err: ', err);
        return err;
      });
      const session$ = from(s);
      this.isAuthenticated$ = session$.pipe(
        filter((sesh) => !!sesh),
        map(
          (sesh) => sesh && typeof sesh.isValid === 'function' && sesh.isValid()
        )
      );

      const token$ = session$.pipe(
        map(
          (sesh) =>
            sesh && typeof sesh.getIdToken === 'function' && sesh.getIdToken()
        )
      );
      this.username$ = token$.pipe(
        map((t) => t && t.payload && t.payload['cognito:username'])
      );
      this.companyName$ = token$.pipe(
        map((t) => t.payload && t.payload['custom:company-name'])
      );
      // POS controls can trigger external calls, so expose them only to tenant/system admins.
      this.isPosAdmin$ = token$.pipe(
        map((t) => ['TenantAdmin', 'SystemAdmin'].includes(t?.payload?.['custom:userRole']))
      );
      this.isTenantAdmin$ = this.isPosAdmin$;

      //set tenant name var to display on" screen
      this.tenantName = localStorage.getItem('tenantName') || ""
      console.log("tenantName:", this.tenantName)

      Hub.listen('auth', this.authHubListener);

    } catch (err) {
      console.error('Unable to get current session.');
    }

  }

  ngOnDestroy(): void {
    Hub.remove('auth', this.authHubListener);
    if (this.entitlementSub) {
      this.entitlementSub.unsubscribe();
    }
    if (this.sessionAllowedSub) {
      this.sessionAllowedSub.unsubscribe();
    }
  }

  // Warm cache and insert any new Func-Settings attributes missing for this tenant.
  private prefetchTenantConfigs(): void {
    this.tenantConfigSvc.fetch().subscribe({
      error: (err) => console.error('Error prefetching tenant configs:', err),
    });
  }

  // Hide off-plan sidebar items and remember home / settings flags from the tenant allowlist.
  // ngOnInit, NavigationEnd, and allowedActionsChanged$ call this.
  private applyPlanEntitlements(): void {
    // Wait for GET /tenant/init (or cached allowlist) before filtering — avoid empty flash without spinner.
    if (!this.tenantEntitlements.areEntitlementsResolved()) {
      this.navPermissionsLoading = true;
      this.navItems = [];
      return;
    }
    this.homeUrl = this.tenantEntitlements.homeUrl();
    this.isTagsAllowed = this.tenantEntitlements.isActionAllowed('ui.tag.nav.settings');
    this.isChatAllowed = this.tenantEntitlements.isActionAllowed('ui.chat.nav');
    this.isChatAdminAllowed = this.tenantEntitlements.isActionAllowed('ui.chat.screen.admin');
    this.isOpsAllowed = this.tenantEntitlements.isActionAllowed('ui.ops.nav.users');
    this.isTenantConfigAllowed = this.tenantEntitlements.isActionAllowed('ui.ops.nav.tenant_config');
    this.isPosAllowed = this.tenantEntitlements.isActionAllowed('ui.pos.nav.settings');
    this.setVisibleNavItems();
  }

  // Paint sidebar from the plan allowlist only.
  // applyPlanEntitlements calls this after entitlements resolve.
  private setVisibleNavItems(): void {
    this.navItems = this.tenantEntitlements.allowedNavItems();
    this.navPermissionsLoading = false;
    this.redirectIfCurrentRouteOffPlan();
  }

  // Bounce from an off-plan deep link to the tenant home URL.
  // setVisibleNavItems and NavigationEnd call this.
  private redirectIfCurrentRouteOffPlan(): void {
    if (!this.tenantEntitlements.isUrlAllowed(this.router.url)) {
      this.router.navigateByUrl(this.homeUrl);
    }
  }

  // Sign out of this browser only; other devices stay connected.
  // Sidebar Logout and the person-menu Sign Out item call this.
  async logout() {
    await this.signOutAndClear(false);
  }

  // Cognito global sign-out so every other connection for this user is dropped too.
  // The person-menu Sign Out Everywhere item calls this.
  async logoutEverywhere() {
    await this.signOutAndClear(true);
  }

  // Shared Cognito sign-out plus local cache cleanup; global is only for Sign Out Everywhere.
  private async signOutAndClear(global: boolean): Promise<void> {
    try {
      if (global) {
        await Auth.signOut({ global: true });
      } else {
        await Auth.signOut();
      }
    } catch (err) {
      console.error('Error logging out: ', err);
    } finally {
      // Always clean up local storage and redirect, regardless of signOut success/failure
      this.supplierService.clearSupplierCache();
      this.tenantConfigSvc.clearTenantConfigCache();
      this.authConfigService.cleanLocalStorage();
      this.router.navigate(['/unauthorized']);
    }
  }
  navigateToSuppliers() {
    this.router.navigate(['/suppliers']);
  }

  // Logo on Amplify sign-in goes to /; skip unauthorized auto-continue so tenant selection stays visible.
  stayOnTenantSelection(): void {
    sessionStorage.setItem('stayOnTenantSelection', '1');
  }
  navigateToTags() {
    this.router.navigate(['/tags']);
  }
  navigateToUsersList() {
    this.router.navigate(['/users']);
  }
  navigateToTenantConfig() {
    this.router.navigate(['/tenant-config/list']);
  }
  navigateToChatAdmin() {
    this.router.navigate(['/chat/admin']);
  }
  // Open the Phase 1 POS onboarding and execution status screen.
  navigateToPosSync() {
    this.router.navigate(['/pos-sync']);
  }
  // Open scheduled task configuration (cron schedules + manual run).
  navigateToScheduledTasks() {
    this.router.navigate(['/scheduled-tasks']);
  }
  openTaxCalculator() {
    this.dialog.open(TaxCalculatorDialogComponent, {
      width: '340px',
    });
  }

  handleToggle() {
    if (this.drawer.opened) {
      // do something before closing
      console.log('Closing sidenav');
    } else {
      // do something before opening
      console.log('Opening sidenav');
    }
  
    this.drawer.toggle();
  }

  closeSidenav() {
    if (this.drawer) {
      this.drawer.close();
    }
  }

  private setNonProdBrowserTitle(): void {
    if (this.isProduction) {
      return;
    }
    this.translate.get('NAV.MENU_PROFIT_MANAGER').subscribe((name) => {
      this.title.setTitle(`[TEST] ${name}`);
    });
  }

  switchLanguage(lang: string) {
    this.translate.use(lang);
    localStorage.setItem('userLang', lang);
    if (lang === 'he') {
      document.documentElement.dir = 'rtl';
      this.direction = 'rtl';
      localStorage.setItem('direction', 'rtl');
    } else {
      document.documentElement.dir = 'ltr';
      this.direction = 'ltr';
      localStorage.setItem('direction', 'ltr');
    }
  }

}
